import React, { PropsWithChildren, createContext, useContext } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { lightV2Theme } from '../../themes';
import type { IMultiTagProps } from '../MultiTag.component';
import { multiTagUtils } from '../utils';
import { StyledMultiTag } from '../types';

const StyledTagContainer = styled.div<StyledMultiTag>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0;
  border-radius: 3px;
  background-color: ${({ backgroundColor }) => backgroundColor};
  border: ${({ borderColor, invalid, theme }) => `1px solid ${invalid ? theme.multiTag.invalid : borderColor}`};
  cursor: ${({ state }) => (state === 'preview' ? 'default' : 'pointer')};
  flex: none;
  overflow-x: hidden;
  scroll-snap-align: start;
  max-width: 100%;

  button {
    color: ${({ theme, state }) => state === 'hovered' && theme.multiTag.closeButton.color.selected};
  }

  &:hover {
    ${({ theme, state }) =>
      state !== 'selected' &&
      state !== 'preview' &&
      css`
        border: 1px solid ${theme.multiTag.container.borderColor.hover};
        background-color: ${theme.multiTag.container.backgroundColor.hover};
      `};

    button {
      color: ${({ theme }) => theme.multiTag.closeButton.color.selected};
    }
  }
`;

type MultiTagContextProps = Omit<IMultiTagProps, 'onClick' | 'onDelete' | 'subject'> & { editMode: boolean };

const MultiTagContext = createContext<MultiTagContextProps | undefined>(undefined);

export const useMultiTag = (): MultiTagContextProps => {
  const ctx = useContext(MultiTagContext);

  if (!ctx) {
    throw new Error('useMultiTag must be used within a MultiTagContext');
  }

  return ctx;
};

export const TagContainer: React.FC<PropsWithChildren<IMultiTagProps & { editMode: boolean }>> = ({
  editMode,
  onSelected,
  state,
  invalid,
  onChange,
  tag,
  highlight,
  noTooltip,
  children,
  isEditable,
  ...props
}) => {
  const {
    multiTag: { container },
  } = useTheme() as typeof lightV2Theme;

  const backgroundColor = multiTagUtils.getBackgroundColor({
    container,
    editMode: editMode ?? false,
    state,
    defaultColor: tag?.style?.backgroundColor,
  });
  const borderColor = multiTagUtils.getBorderColor(container, state);

  return (
    <MultiTagContext.Provider
      value={{
        editMode: editMode ?? false,
        onSelected,
        state,
        invalid,
        onChange,
        tag,
        highlight,
        noTooltip,
        isEditable,
      }}
    >
      <StyledTagContainer
        {...{ state, backgroundColor, borderColor, invalid, ...props }}
        {...(state !== 'preview' && { onClick: onSelected })}
        data-state={state}
      >
        {children}
      </StyledTagContainer>
    </MultiTagContext.Provider>
  );
};
